import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Divider, Select, Collapse,
  Table, Thead, Tbody, Tr, Th, Td, Tabs, TabList, Tab, TabPanels, TabPanel,
  Tooltip, Menu, MenuButton, MenuList, MenuItem, Image,
} from '@chakra-ui/react';
import { FiPlus, FiUpload, FiChevronDown, FiChevronRight, FiX, FiDownload, FiTrash2, FiEdit2, FiExternalLink, FiMoreVertical, FiFolder, FiFolderPlus, FiCornerUpRight, FiMove } from 'react-icons/fi';
import api from '../api';

const DOC_ACCEPT = [
  // documents
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.rtf', '.odt', '.ods', '.odp', '.md',
  // images (incl. modern web formats)
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.svg', '.bmp', '.tiff', '.tif', '.heic', '.heif',
  // video
  '.mp4', '.mov', '.webm', '.avi', '.mkv', '.mpeg', '.mpg', '.m4v', '.ogv', '.3gp',
  // audio / music
  '.mp3', '.wav', '.ogg', '.oga', '.aac', '.flac', '.m4a', '.opus', '.weba',
  // archives / compressed
  '.zip', '.7z', '.rar', '.gz', '.tar', '.tgz', '.bz2',
].join(',');

// Source type config
const SOURCE_CONFIG = {
  document:      { label: 'Document',      color: 'teal',   icon: '📄' },
  invoice:       { label: 'Invoice',        color: 'green',  icon: '💷' },
  note:          { label: 'Note',           color: 'purple', icon: '📝' },
  communication: { label: 'Communication', color: 'blue',   icon: '✉️' },
};

// ── Drag-and-drop zone: wraps any picker area; dropping a file feeds the
// same state as Choose File. One file per document — extra drops warn.
function DropZone({ onFile, accent = 'teal', children, toast }) {
  const [over, setOver] = React.useState(false);
  return (
    <Box
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragLeave={e => { e.preventDefault(); setOver(false); }}
      onDrop={e => {
        e.preventDefault(); e.stopPropagation(); setOver(false);
        const files = Array.from(e.dataTransfer?.files || []);
        if (!files.length) return;
        if (files.length > 1 && toast) toast({ title: 'One file per document — using the first', status: 'info', duration: 2200 });
        onFile(files[0]);
      }}
      border="2px dashed"
      borderColor={over ? `${accent}.400` : 'gray.200'}
      bg={over ? `${accent}.50` : 'transparent'}
      borderRadius="md" p={3} transition="all 0.15s"
    >
      {children}
      <Text fontSize="10px" color={over ? `${accent}.600` : 'gray.400'} mt={1.5}>
        {over ? 'Drop the file here' : 'Drag and drop a file here, or use Choose File'}
      </Text>
    </Box>
  );
}

export default function DocumentsModal({ projectId, taskId, isOpen, onClose, onOpenComms, onOpenNotes, onOpenInvoice, initialDoc }) {
  const [families, setFamilies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [versioningId, setVersioningId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allFiles, setAllFiles] = useState([]);
  const [allFilesLoading, setAllFilesLoading] = useState(false);
  const [addVersionTarget, setAddVersionTarget] = useState(null); // file to add as doc version
  const [activeTab, setActiveTab] = useState(0);
  const fileRef = useRef(null);
  const versionFileRef = useRef(null);
  const addVersionFileRef = useRef(null);
  const toast = useToast();

  const emptyForm = { name: '', description: '', user_ids: [], contact_ids: [], file: null, fileId: null, external_url: '' };
  const [form, setForm] = useState(emptyForm);
  const [versionForm, setVersionForm] = useState({ file: null, fileId: null, change_notes: '', external_url: '', user_ids: [], contact_ids: [] });

  // ── Folders ────────────────────────────────────────────────────────────
  const [folders, setFolders] = useState([]);
  const [collapsedFolders, setCollapsedFolders] = useState({}); // folderId -> true (collapsed)
  const [ctxMenu, setCtxMenu] = useState(null);     // { x, y, doc }
  const [folderModal, setFolderModal] = useState(null); // { name, description, editId? }
  const [savingFolder, setSavingFolder] = useState(false);
  const dragDocId = useRef(null);     // id of document family being dragged
  const dragFolderId = useRef(null);  // uuid of folder being reordered
  const [dragOverFolder, setDragOverFolder] = useState(undefined); // folder uuid | null(root) being hovered
  // Folders created during this modal session. When the modal is opened for
  // a specific task, the Documents tab hides project folders that hold no
  // documents for that task — but a folder the user just created must stay
  // visible (it's empty by definition) so they can file documents into it.
  const sessionFolderIds = useRef(new Set());

  const fetchFolders = async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/documents/folders?project_id=${projectId}`);
      setFolders(Array.isArray(data) ? data : []);
    } catch(e) { setFolders([]); }
  };

  const moveDocToFolder = async (docId, folderId) => {
    try {
      await api.put(`/documents/${docId}/move`, { folder_id: folderId || null });
      await fetchDocs();
    } catch(e) { toast({ title: 'Could not move document', status: 'error', duration: 2000 }); }
  };

  const saveFolder = async () => {
    if (!folderModal || !folderModal.name.trim()) return;
    setSavingFolder(true);
    try {
      if (folderModal.editId) {
        await api.put(`/documents/folders/${folderModal.editId}`, { name: folderModal.name.trim(), description: folderModal.description || '' });
      } else {
        const { data } = await api.post('/documents/folders', { project_id: projectId, name: folderModal.name.trim(), description: folderModal.description || '' });
        if (data?.id) sessionFolderIds.current.add(data.id);
        // If a document was queued to move into the new folder, move it now
        if (folderModal.moveDocId && data?.id) await moveDocToFolder(folderModal.moveDocId, data.id);
      }
      await fetchFolders();
      setFolderModal(null);
    } catch(e) { toast({ title: 'Could not save folder', status: 'error', duration: 2000 }); }
    finally { setSavingFolder(false); }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Documents inside it will become unfiled.')) return;
    try {
      await api.delete(`/documents/folders/${folderId}`);
      await Promise.all([fetchFolders(), fetchDocs()]);
    } catch(e) { toast({ title: 'Could not delete folder', status: 'error', duration: 2000 }); }
  };

  // Reorder folders locally (live shuffle while dragging) and persist on drop
  const reorderFoldersLocal = (sourceId, targetId) => {
    if (sourceId === targetId) return;
    setFolders(prev => {
      const arr = [...prev];
      const from = arr.findIndex(f => f.id === sourceId);
      const to = arr.findIndex(f => f.id === targetId);
      if (from < 0 || to < 0) return prev;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  const persistFolderOrder = async () => {
    try { await api.put('/documents/folders/reorder', { folder_ids: folders.map(f => f.id) }); }
    catch(e) { fetchFolders(); }
  };

  const fetchDocs = async () => {
    if (!projectId) return;
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const { data } = await api.get(`/documents?project_id=${projectId}${taskParam}`);
      setFamilies(data);
    } catch(e) {}
  };

  const fetchAllFiles = async () => {
    if (!projectId) return;
    setAllFilesLoading(true);
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const { data } = await api.get(`/documents/all-files?project_id=${projectId}${taskParam}`);
      setAllFiles(data);
    } catch(e) { setAllFiles([]); }
    finally { setAllFilesLoading(false); }
  };

  const fetchMeta = async () => {
    try {
      const [uRes, ctRes] = await Promise.all([
        api.get('/calendar/users').catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/contacts`).catch(() => ({ data: [] })),
      ]);
      setAllUsers(uRes.data || []);
      setAllContacts(ctRes.data || []);
    } catch(e) {}
  };

  useEffect(() => {
    if (isOpen && projectId) {
      fetchDocs();
      fetchAllFiles();
      fetchMeta();
      fetchFolders();
      setCreating(false);
      setVersioningId(null);
      setEditingId(null);
      setForm(emptyForm);
      setActiveTab(0);
      sessionFolderIds.current = new Set();
    }
  }, [isOpen, projectId]); // eslint-disable-line

  // Prefilled entry point (e.g. "save email attachment as document"): the
  // file already exists server-side, so the form opens with it PRESELECTED
  // — fileId set, no re-upload — and the user just names/describes/assigns.
  useEffect(() => {
    if (isOpen && initialDoc && initialDoc.fileId) {
      setCreating(true);
      setForm({
        ...emptyForm,
        name: initialDoc.name || '',
        fileId: initialDoc.fileId,
        existingFileName: initialDoc.fileName || 'attached file',
        existingFileSize: initialDoc.fileSize || null,
      });
    }
    // eslint-disable-next-line
  }, [isOpen, initialDoc]);

  // Upload a file and return its file_id
  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'documents');
      fd.append('entity_type', 'project_document');
      fd.append('entity_id', projectId);
      const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.id;
    } catch(e) {
      toast({ title: 'File upload failed', status: 'error', duration: 2000 });
      return null;
    } finally { setUploading(false); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast({ title: 'Document name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      let fileId = form.fileId;
      if (form.file && !fileId) {
        fileId = await uploadFile(form.file);
        if (!fileId) { setSaving(false); return; }
      }
      await api.post('/documents', {
        project_id: projectId, task_id: taskId || null,
        name: form.name, description: form.description,
        file_id: fileId, external_url: (form.external_url || '').trim() || null,
        user_ids: form.user_ids, contact_ids: form.contact_ids,
      });
      toast({ title: 'Document created', status: 'success', duration: 1500 });
      setCreating(false); setForm(emptyForm);
      await fetchDocs();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleVersion = async (currentDoc) => {
    setSaving(true);
    try {
      let fileId = versionForm.fileId;
      if (versionForm.file && !fileId) {
        fileId = await uploadFile(versionForm.file);
        if (!fileId) { setSaving(false); return; }
      }
      const versionLink = (versionForm.external_url || '').trim();
      if (!fileId && !versionLink) { toast({ title: 'Add a file or a link for the new version', status: 'warning', duration: 2000 }); setSaving(false); return; }
      await api.post(`/documents/${currentDoc.id}/version`, {
        file_id: fileId,
        change_notes: versionForm.change_notes,
        external_url: versionLink || null,
        user_ids: versionForm.user_ids.length ? versionForm.user_ids : undefined,
        contact_ids: versionForm.contact_ids.length ? versionForm.contact_ids : undefined,
      });
      toast({ title: `Version ${(currentDoc.version || 1) + 1} uploaded`, status: 'success', duration: 1800 });
      setVersioningId(null); setVersionForm({ file: null, fileId: null, change_notes: '', external_url: '', user_ids: [], contact_ids: [] });
      await fetchDocs();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleEdit = async (docId) => {
    setSaving(true);
    try {
      await api.put(`/documents/${docId}`, {
        name: form.name, description: form.description,
        user_ids: form.user_ids, contact_ids: form.contact_ids,
      });
      toast({ title: 'Updated', status: 'success', duration: 1500 });
      setEditingId(null); setForm(emptyForm);
      await fetchDocs();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      toast({ title: 'Deleted', status: 'info', duration: 1500 });
      await fetchDocs();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const openEdit = (doc) => {
    setEditingId(doc.id);
    setVersioningId(null);
    setCreating(false);
    setForm({
      name: doc.name, description: doc.description || '',
      user_ids: (doc.users || []).map(u => u.user_id),
      contact_ids: (doc.contacts || []).map(c => c.contact_id),
      file: null, fileId: null,
    });
  };

  const openVersioning = (doc) => {
    setVersioningId(doc.id);
    setEditingId(null);
    setCreating(false);
    setVersionForm({
      file: null, fileId: null, change_notes: '',
      user_ids: (doc.users || []).map(u => u.user_id),
      contact_ids: (doc.contacts || []).map(c => c.contact_id),
    });
  };

  const toggleUser = (uid, target, setter) => setter(p => ({ ...p, user_ids: p.user_ids.map(String).includes(String(uid)) ? p.user_ids.filter(x => String(x) !== String(uid)) : [...p.user_ids, uid] }));
  const toggleContact = (cid, target, setter) => setter(p => ({ ...p, contact_ids: p.contact_ids.map(String).includes(String(cid)) ? p.contact_ids.filter(x => String(x) !== String(cid)) : [...p.contact_ids, cid] }));

  const AssigneeEditor = ({ value, setter }) => (
    <VStack spacing={2} align="stretch">
      <Box>
        <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Assign Users</Text>
        <Flex gap={1} flexWrap="wrap" mb={1}>
          {allUsers.filter(u => value.user_ids.map(String).includes(String(u.id))).map(u => (
            <Badge key={u.id} colorScheme="teal" fontSize="10px" display="flex" alignItems="center" gap={1}>
              {u.display_name || u.username}
              <IconButton icon={<FiX />} size="xs" variant="ghost" minW="12px" h="12px" onClick={() => toggleUser(u.id, value, setter)} aria-label="Remove" />
            </Badge>
          ))}
        </Flex>
        <Select size="xs" placeholder="Add user..." value="" onChange={e => { if (e.target.value) toggleUser(e.target.value, value, setter); }}>
          {allUsers.filter(u => !value.user_ids.map(String).includes(String(u.id))).map(u => (
            <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
          ))}
        </Select>
      </Box>
      <Box>
        <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Assign Contacts</Text>
        <Flex gap={1} flexWrap="wrap" mb={1}>
          {allContacts.filter(c => value.contact_ids.map(String).includes(String(c.id || c.contact_id))).map(c => (
            <Badge key={c.id || c.contact_id} colorScheme="purple" fontSize="10px" display="flex" alignItems="center" gap={1}>
              {c.first_name} {c.last_name || ''}
              <IconButton icon={<FiX />} size="xs" variant="ghost" minW="12px" h="12px" onClick={() => toggleContact(c.id || c.contact_id, value, setter)} aria-label="Remove" />
            </Badge>
          ))}
        </Flex>
        <Select size="xs" placeholder="Add contact..." value="" onChange={e => { if (e.target.value) toggleContact(e.target.value, value, setter); }}>
          {allContacts.filter(c => !value.contact_ids.map(String).includes(String(c.id || c.contact_id))).map(c => (
            <option key={c.id || c.contact_id} value={c.id || c.contact_id}>{c.first_name} {c.last_name || ''}</option>
          ))}
        </Select>
      </Box>
    </VStack>
  );

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtSize = (b) => { if (!b) return ''; if (b < 1024) return `${b}B`; if (b < 1048576) return `${(b/1024).toFixed(0)}KB`; return `${(b/1048576).toFixed(1)}MB`; };

  // ── Helpers for All Files view ────────────────────────────────────────────
  const fileUrl = (f) => f.stored_name && f.folder
    ? `/api/files/${f.folder}/${f.stored_name}`
    : null;

  // Classify a document/file for inline preview. Uses mime_type when present,
  // else falls back to the file extension.
  const mediaKind = (f) => {
    const mime = (f.mime_type || '').toLowerCase();
    const name = (f.original_name || f.stored_name || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() : '';
    if (mime.startsWith('image/') || ['png','jpg','jpeg','gif','webp','avif','svg','bmp','tiff','tif','heic','heif'].includes(ext)) return 'image';
    if (mime.startsWith('audio/') || ['mp3','wav','ogg','oga','aac','flac','m4a','opus','weba'].includes(ext)) return 'audio';
    if (mime.startsWith('video/') || ['mp4','mov','webm','avi','mkv','mpeg','mpg','m4v','ogv','3gp'].includes(ext)) return 'video';
    return 'other';
  };

  const fmtDate2 = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';

  const mimeIcon = (mime) => {
    if (!mime) return '📄';
    if (mime.startsWith('image/')) return '🖼️';
    if (mime === 'application/pdf') return '📕';
    if (mime.includes('word') || mime.includes('document')) return '📝';
    if (mime.includes('excel') || mime.includes('sheet')) return '📊';
    if (mime.includes('zip') || mime.includes('compressed')) return '🗜️';
    if (mime.startsWith('video/')) return '🎬';
    return '📄';
  };

  const handleAddAsVersion = async (file) => {
    // Prompt user to pick a document family or create a new one, then register this file
    setAddVersionTarget(file);
  };

  const handleConfirmAddVersion = async (docFamilyName, changeNotes) => {
    if (!addVersionTarget) return;
    const f = addVersionTarget;
    setSaving(true);
    try {
      // Check if a document with this name already exists
      const existingFamily = families.find(fam => fam.current.name.toLowerCase() === docFamilyName.toLowerCase());
      if (existingFamily) {
        // Add as a new version of the existing document
        await api.post(`/documents/${existingFamily.current.id}/version`, {
          file_id: f.file_id,
          change_notes: changeNotes || `Imported from ${SOURCE_CONFIG[f.source_type]?.label || f.source_type}: ${f.source_subject || f.original_name}`,
        });
        toast({ title: `Added as v${(existingFamily.current.version || 1) + 1} of "${docFamilyName}"`, status: 'success', duration: 2000 });
      } else {
        // Create a new document entry
        await api.post('/documents', {
          project_id: projectId, task_id: taskId || null,
          name: docFamilyName,
          description: `From ${SOURCE_CONFIG[f.source_type]?.label || f.source_type}`,
          file_id: f.file_id,
        });
        toast({ title: `Created new document "${docFamilyName}"`, status: 'success', duration: 2000 });
      }
      setAddVersionTarget(null);
      await fetchDocs();
      await fetchAllFiles();
    } catch(e) {
      toast({ title: 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleNavigateToSource = (file) => {
    onClose();
    if (file.source_type === 'communication' && onOpenComms) onOpenComms();
    else if (file.source_type === 'note' && onOpenNotes) onOpenNotes();
    else if (file.source_type === 'invoice' && onOpenInvoice) onOpenInvoice(file.source_direction === 'in' ? 'in' : 'out');
  };

  // ── AddVersionDialog ────────────────────────────────────────────────────────
  const AddVersionDialog = () => {
    const [docName, setDocName] = useState(addVersionTarget?.original_name?.replace(/\.[^.]+$/, '') || '');
    const [notes, setNotes] = useState('');
    const existingNames = families.map(f => f.current.name);
    return (
      <Box position="fixed" top="50%" left="50%" transform="translate(-50%,-50%)"
        bg="white" border="1px" borderColor="gray.200" borderRadius="xl" boxShadow="2xl"
        p={5} zIndex={10001} w="400px">
        <Text fontSize="13px" fontWeight="700" mb={1}>Add "{addVersionTarget?.original_name}" as a Document</Text>
        <Text fontSize="10px" color="gray.500" mb={3}>
          Choose an existing document to add as a new version, or type a new name to create one.
        </Text>
        <FormControl mb={2}>
          <FormLabel fontSize="xs" mb={0.5}>Document name</FormLabel>
          <Input size="sm" value={docName} onChange={e => setDocName(e.target.value)} list="doc-names-list" placeholder="Type or choose…" />
          <datalist id="doc-names-list">
            {existingNames.map(n => <option key={n} value={n} />)}
          </datalist>
          {existingNames.includes(docName) && (
            <Text fontSize="9px" color="teal.600" mt={0.5}>↑ Will be added as a new version of this document</Text>
          )}
          {docName && !existingNames.includes(docName) && (
            <Text fontSize="9px" color="blue.600" mt={0.5}>↑ Will create a new document with this name</Text>
          )}
        </FormControl>
        <FormControl mb={3}>
          <FormLabel fontSize="xs" mb={0.5}>Change notes <Text as="span" color="gray.400">(optional)</Text></FormLabel>
          <Input size="sm" value={notes} onChange={e => setNotes(e.target.value)} placeholder="What does this file represent?" />
        </FormControl>
        <HStack justify="flex-end" spacing={2}>
          <Button size="sm" variant="ghost" onClick={() => setAddVersionTarget(null)}>Cancel</Button>
          <Button size="sm" colorScheme="teal" isDisabled={!docName.trim()} isLoading={saving}
            onClick={() => handleConfirmAddVersion(docName.trim(), notes)}>
            {existingNames.includes(docName) ? 'Add New Version' : 'Create Document'}
          </Button>
        </HStack>
      </Box>
    );
  };

  // ── Build an ordered render list interleaving folder headers + families ──
  const familiesByFolder = {};
  // Supporting documents (doc_kind 'invoice_support') sit beneath the invoice
  // document they evidence, in the same folder.
  const childrenOf = {};
  families.forEach(f => { if (f.current.parent_document_id) (childrenOf[f.current.parent_document_id] = childrenOf[f.current.parent_document_id] || []).push(f); });
  families.forEach(f => {
    if (f.current.parent_document_id && families.some(p => p.current.id === f.current.parent_document_id)) return;   // rendered under its parent
    const key = f.current.folder_id || '__root__';
    (familiesByFolder[key] = familiesByFolder[key] || []).push(f);
    for (const c of childrenOf[f.current.id] || []) (familiesByFolder[key] = familiesByFolder[key] || []).push({ ...c, _nested: true });
  });
  // Folders are PROJECT-scoped rows, but when the modal is opened from a
  // task the document list is task-filtered. Rendering every project folder
  // produced a wall of empty folders that belong to other tasks — so in
  // task scope we only show folders that actually contain this task's
  // documents, plus any folder created in this session (empty until the
  // user files something into it). Project scope (no taskId) is unchanged.
  // The move/"file into folder" context menu still lists ALL project
  // folders, so documents can be filed into folders hidden here.
  const visibleFolders = taskId
    ? folders.filter(f => (familiesByFolder[f.id] || []).length > 0 || sessionFolderIds.current.has(f.id))
    : folders;
  const orderedDocItems = [];
  visibleFolders.forEach(folder => {
    orderedDocItems.push({ _folderHeader: { folder, count: (familiesByFolder[folder.id] || []).length } });
    if (!collapsedFolders[folder.id]) (familiesByFolder[folder.id] || []).forEach(f => orderedDocItems.push(f));
  });
  const rootFamilies = familiesByFolder['__root__'] || [];
  if (visibleFolders.length > 0) orderedDocItems.push({ _folderHeader: { folder: null, count: rootFamilies.length } });
  rootFamilies.forEach(f => orderedDocItems.push(f));

  const onDocDragStart = (docId) => (e) => { dragDocId.current = docId; dragFolderId.current = null; e.dataTransfer.effectAllowed = 'move'; };
  const onFolderDrop = (folderId) => async (e) => {
    e.preventDefault(); setDragOverFolder(undefined);
    if (dragDocId.current != null) { const id = dragDocId.current; dragDocId.current = null; await moveDocToFolder(id, folderId); }
    else if (dragFolderId.current) { dragFolderId.current = null; await persistFolderOrder(); }
  };

  const renderFolderHeader = ({ folder, count }) => {
    const isRoot = !folder;
    const collapsed = !isRoot && collapsedFolders[folder.id];
    const isOver = dragOverFolder === (isRoot ? null : folder.id);
    return (
      <Flex key={'fh-' + (isRoot ? 'root' : folder.id)} align="center" gap={2} px={3} py={1.5} mt={isRoot ? 2 : 0}
        bg={isOver ? 'teal.50' : (isRoot ? 'transparent' : 'blue.50')}
        border={isOver ? '2px dashed' : '1px solid'} borderColor={isOver ? 'teal.400' : (isRoot ? 'gray.100' : 'blue.100')}
        borderRadius="md"
        draggable={!isRoot}
        onDragStart={!isRoot ? (e) => { dragFolderId.current = folder.id; dragDocId.current = null; e.dataTransfer.effectAllowed = 'move'; } : undefined}
        onDragEnd={!isRoot ? () => { if (dragFolderId.current) { dragFolderId.current = null; persistFolderOrder(); } } : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          if (dragDocId.current != null) setDragOverFolder(isRoot ? null : folder.id);
          else if (dragFolderId.current && !isRoot && dragFolderId.current !== folder.id) reorderFoldersLocal(dragFolderId.current, folder.id);
        }}
        onDragLeave={() => { if (dragDocId.current != null) setDragOverFolder(undefined); }}
        onDrop={onFolderDrop(isRoot ? null : folder.id)}
      >
        {!isRoot && <Box color="gray.400" cursor="grab" title="Drag to reorder"><FiMove size={12} /></Box>}
        {isRoot ? (
          <>
            <FiCornerUpRight size={13} />
            <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" flex={1}>Unfiled</Text>
          </>
        ) : (
          <>
            <Box as="span" cursor="pointer" onClick={() => setCollapsedFolders(p => ({ ...p, [folder.id]: !p[folder.id] }))}>
              {collapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
            </Box>
            <Box color="blue.500"><FiFolder size={14} /></Box>
            <Box minW={0} flex={1}>
              <Text fontSize="12px" fontWeight="700" isTruncated>{folder.name}</Text>
              {!!folder.description && <Text fontSize="9px" color="gray.500" isTruncated>{folder.description}</Text>}
            </Box>
          </>
        )}
        <Badge colorScheme={isRoot ? 'gray' : 'blue'} fontSize="9px">{count}</Badge>
        {!isRoot && (
          <HStack spacing={0.5}>
            <IconButton icon={<FiEdit2 size={11} />} size="xs" variant="ghost" aria-label="Edit folder"
              onClick={() => setFolderModal({ name: folder.name, description: folder.description || '', editId: folder.id })} />
            <IconButton icon={<FiTrash2 size={11} />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete folder"
              onClick={() => deleteFolder(folder.id)} />
          </HStack>
        )}
      </Flex>
    );
  };

  return (
    <>
    {addVersionTarget && (
      <>
        <Box position="fixed" inset={0} bg="blackAlpha.400" zIndex={10000} onClick={() => setAddVersionTarget(null)} />
        <AddVersionDialog />
      </>
    )}
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontSize="md" pb={0}>
          <Flex justify="space-between" pr={8} align="center">
            <Text>Documents & Files</Text>
            {activeTab === 0 && (
              <HStack spacing={2}>
                <Button size="xs" leftIcon={<FiFolderPlus />} colorScheme="blue" variant="outline"
                  onClick={() => setFolderModal({ name: '', description: '' })}>
                  New Folder
                </Button>
                <Button size="xs" leftIcon={<FiPlus />} colorScheme="teal" variant={creating ? 'solid' : 'outline'}
                  onClick={() => { setCreating(!creating); setEditingId(null); setVersioningId(null); setForm(emptyForm); }}>
                  {creating ? 'Cancel' : 'New Document'}
                </Button>
              </HStack>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px={0} pt={0}>
          <Tabs index={activeTab} onChange={setActiveTab} variant="line" colorScheme="teal" size="sm">
            <TabList px={4} borderBottom="2px solid" borderColor="gray.100">
              <Tab fontSize="12px" fontWeight="600">
                📄 Documents
                {families.length > 0 && <Badge ml={1} colorScheme="teal" borderRadius="full" fontSize="9px">{families.length}</Badge>}
              </Tab>
              <Tab fontSize="12px" fontWeight="600">
                🗂️ All Files
                {allFiles.length > 0 && <Badge ml={1} colorScheme="blue" borderRadius="full" fontSize="9px">{allFiles.length}</Badge>}
              </Tab>
            </TabList>

            <TabPanels>
            {/* ── Tab 1: Documents (existing UI) ── */}
            <TabPanel px={4}>
              <VStack spacing={3} align="stretch">

            {/* ── Create form ── */}
            {creating && (
              <Box bg="teal.50" p={4} borderRadius="md" border="1px" borderColor="teal.200">
                <Text fontSize="10px" fontWeight="700" color="teal.700" textTransform="uppercase" mb={3}>New Document</Text>
                <VStack spacing={3}>
                  <HStack w="100%" spacing={2}>
                    <FormControl flex={2}>
                      <FormLabel fontSize="xs" mb={0.5}>Document Name *</FormLabel>
                      <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Production Schedule, Script v1..." autoFocus />
                    </FormControl>
                  </HStack>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                    <Textarea size="sm" rows={2} value={form.description}
                      onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Brief description of this document..." />
                  </FormControl>

                  {/* File picker */}
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>File <Text as="span" color="gray.400">(optional — can be added later)</Text></FormLabel>
                    <input type="file" accept={DOC_ACCEPT} ref={fileRef} style={{ display: 'none' }}
                      onChange={e => { const f = e.target.files[0]; if (f) setForm(p => ({ ...p, file: f, fileId: null })); }} />
                    <DropZone toast={toast} accent="teal"
                      onFile={f => setForm(p => ({ ...p, file: f, fileId: null, existingFileName: null }))}>
                      <HStack flexWrap="wrap">
                        <Button size="xs" leftIcon={<FiUpload />} variant="outline" onClick={() => fileRef.current?.click()}>
                          Choose File
                        </Button>
                        {form.file && (
                          <Text fontSize="11px" color="teal.700" fontWeight="600">
                            {form.file.name} ({fmtSize(form.file.size)})
                          </Text>
                        )}
                        {!form.file && form.fileId && form.existingFileName && (
                          <Text fontSize="11px" color="green.700" fontWeight="600" bg="green.50" px={2} py={0.5} borderRadius="md">
                            ✓ {form.existingFileName}{form.existingFileSize ? ` (${fmtSize(form.existingFileSize)})` : ''} — already uploaded, no re-upload needed
                          </Text>
                        )}
                      </HStack>
                    </DropZone>
                  </FormControl>

                  {/* Link — live online URL for the content (any type) */}
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Link <Text as="span" color="gray.400">(optional — a live online URL to this content)</Text></FormLabel>
                    <Input size="sm" value={form.external_url}
                      onChange={e => setForm(p => ({ ...p, external_url: e.target.value }))}
                      placeholder="https://…" />
                  </FormControl>

                  <AssigneeEditor value={form} setter={setForm} />

                  <HStack justify="flex-end" w="100%">
                    <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setForm(emptyForm); }}>Cancel</Button>
                    <Button size="sm" bg="teal.500" color="white" _hover={{ bg: 'teal.600' }}
                      isLoading={saving || uploading} onClick={handleCreate}>
                      Create Document
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            )}

            {/* ── Document families ── */}
            {families.length === 0 && folders.length === 0 && !creating && (
              <Box textAlign="center" py={10} color="gray.400">
                <Text fontSize="13px">No documents yet.</Text>
                <Text fontSize="11px" mt={1}>Click "New Document" to add one.</Text>
              </Box>
            )}

            {orderedDocItems.map((item, _idx) => {
              if (item._folderHeader) return renderFolderHeader(item._folderHeader);
              const { current, history } = item;
              return (
              <Box key={current.id} border="1px" borderColor={item._nested ? 'purple.100' : 'gray.200'} borderRadius="md" overflow="hidden" ml={item._nested ? 6 : 0} borderLeft={item._nested ? '3px solid' : undefined} borderLeftColor={item._nested ? 'purple.300' : undefined}
                draggable={editingId !== current.id && versioningId !== current.id}
                onDragStart={onDocDragStart(current.id)}
                onDragEnd={() => { dragDocId.current = null; setDragOverFolder(undefined); }}
                onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, doc: current }); }}>
                {/* Current version header */}
                <Flex
                  bg={editingId === current.id ? 'blue.50' : 'gray.50'}
                  px={3} py={2} align="center" justify="space-between"
                  borderBottom={versioningId === current.id || editingId === current.id || history.length > 0 ? '1px' : 'none'}
                  borderColor="gray.200"
                >
                  <HStack spacing={3} flex={1} minW={0}>
                    <Badge colorScheme="teal" fontSize="10px" flexShrink={0}>v{current.version}</Badge>
                    {current.doc_kind === 'invoice' && <Badge colorScheme="red" fontSize="9px" flexShrink={0}>invoice{current.invoice_number ? ` ${current.invoice_number}` : ''}</Badge>}
                    {current.doc_kind === 'invoice_support' && <Badge colorScheme="purple" fontSize="9px" flexShrink={0}>supporting</Badge>}
                    <Box minW={0}>
                      <Text fontSize="13px" fontWeight="700" isTruncated>{current.name}</Text>
                      {current.description && (
                        <Text fontSize="10px" color="gray.500" isTruncated>{current.description}</Text>
                      )}
                    </Box>
                    {/* Assignee badges */}
                    <Flex gap={1} flexWrap="nowrap" flexShrink={0}>
                      {(current.users || []).map(u => (
                        <Badge key={u.user_id} colorScheme="teal" fontSize="8px">{u.display_name || u.username}</Badge>
                      ))}
                      {(current.contacts || []).map(c => (
                        <Badge key={c.contact_id} colorScheme="purple" fontSize="8px">{c.first_name} {c.last_name || ''}</Badge>
                      ))}
                    </Flex>
                  </HStack>
                  <HStack spacing={1} flexShrink={0} ml={2}>
                    {current.stored_name && (
                      <Button as="a" href={fileUrl(current)} target="_blank" size="xs" variant="ghost"
                        leftIcon={<FiDownload size={12} />} colorScheme="blue" download={current.original_name}>
                        {current.original_name ? current.original_name.split('.').pop().toUpperCase() : 'File'}
                      </Button>
                    )}
                    {current.external_url && (
                      <Button as="a" href={current.external_url} target="_blank" rel="noopener noreferrer" size="xs" variant="ghost"
                        leftIcon={<FiExternalLink size={12} />} colorScheme="purple">
                        Open link
                      </Button>
                    )}
                    <Button size="xs" variant="ghost" leftIcon={<FiUpload size={12} />} colorScheme="orange"
                      onClick={() => versioningId === current.id ? setVersioningId(null) : openVersioning(current)}>
                      New Version
                    </Button>
                    <IconButton icon={<FiEdit2 size={13} />} size="xs" variant="ghost" colorScheme="blue"
                      onClick={() => editingId === current.id ? setEditingId(null) : openEdit(current)} aria-label="Edit" />
                    <IconButton icon={<FiTrash2 size={13} />} size="xs" variant="ghost" colorScheme="red"
                      onClick={() => handleDelete(current.id)} aria-label="Delete" />
                  </HStack>
                </Flex>

                {/* File info row */}
                {current.original_name && (
                  <Flex bg="white" px={3} py={1} align="center" gap={3} borderBottom="1px" borderColor="gray.100">
                    <Text fontSize="10px" color="gray.500">{current.original_name}</Text>
                    {current.file_size > 0 && <Text fontSize="9px" color="gray.400">{fmtSize(current.file_size)}</Text>}
                    <Text fontSize="9px" color="gray.400">Uploaded {fmtDate(current.created_at)}</Text>
                    {current.created_by_name && <Text fontSize="9px" color="gray.400">by {current.created_by_name}</Text>}
                    {current.change_notes && (
                      <Text fontSize="10px" color="orange.600" fontStyle="italic">"{current.change_notes}"</Text>
                    )}
                  </Flex>
                )}

                {/* Link-only row (no uploaded file) */}
                {!current.original_name && current.external_url && (
                  <Flex bg="white" px={3} py={1} align="center" gap={2} borderBottom="1px" borderColor="gray.100">
                    <FiExternalLink size={11} />
                    <Text as="a" href={current.external_url} target="_blank" rel="noopener noreferrer"
                      fontSize="10px" color="purple.600" isTruncated>{current.external_url}</Text>
                  </Flex>
                )}

                {/* Inline preview / play — image, audio, video */}
                {(() => {
                  const url = fileUrl(current);
                  if (!url) return null;
                  const kind = mediaKind(current);
                  if (kind === 'image') return (
                    <Box bg="gray.50" px={3} py={2} borderBottom="1px" borderColor="gray.100" textAlign="center">
                      <Image src={url} maxH="260px" mx="auto" borderRadius="md" objectFit="contain" />
                    </Box>
                  );
                  if (kind === 'audio') return (
                    <Box bg="gray.50" px={3} py={2} borderBottom="1px" borderColor="gray.100">
                      <audio controls preload="metadata" src={url} style={{ width: '100%' }} />
                    </Box>
                  );
                  if (kind === 'video') return (
                    <Box bg="black" px={3} py={2} borderBottom="1px" borderColor="gray.100" textAlign="center">
                      <video controls preload="metadata" src={url} style={{ maxHeight: '320px', maxWidth: '100%', background: 'black' }} />
                    </Box>
                  );
                  return null;
                })()}

                {/* Edit form */}
                {editingId === current.id && (
                  <Box bg="blue.50" p={3} borderBottom="1px" borderColor="gray.200">
                    <Text fontSize="9px" fontWeight="700" color="blue.700" textTransform="uppercase" mb={2}>Edit Document</Text>
                    <VStack spacing={2}>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                        <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                        <Textarea size="sm" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                      </FormControl>
                      <AssigneeEditor value={form} setter={setForm} />
                      <HStack justify="flex-end" w="100%">
                        <Button size="xs" variant="ghost" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancel</Button>
                        <Button size="xs" colorScheme="blue" isLoading={saving} onClick={() => handleEdit(current.id)}>Save</Button>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* New version form */}
                {versioningId === current.id && (
                  <Box bg="orange.50" p={3} borderBottom="1px" borderColor="gray.200">
                    <Text fontSize="9px" fontWeight="700" color="orange.700" textTransform="uppercase" mb={2}>
                      Upload Version {(current.version || 1) + 1}
                    </Text>
                    <VStack spacing={2}>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>New File <Text as="span" color="gray.400">(file or link)</Text></FormLabel>
                        <input type="file" accept={DOC_ACCEPT} ref={versionFileRef} style={{ display: 'none' }}
                          onChange={e => { const f = e.target.files[0]; if (f) setVersionForm(p => ({ ...p, file: f, fileId: null })); }} />
                        <DropZone toast={toast} accent="orange"
                          onFile={f => setVersionForm(p => ({ ...p, file: f, fileId: null }))}>
                          <HStack>
                            <Button size="xs" leftIcon={<FiUpload />} variant="outline" colorScheme="orange"
                              onClick={() => versionFileRef.current?.click()}>
                              Choose File
                            </Button>
                            {versionForm.file && (
                              <Text fontSize="11px" color="orange.700" fontWeight="600">
                                {versionForm.file.name} ({fmtSize(versionForm.file.size)})
                              </Text>
                            )}
                          </HStack>
                        </DropZone>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Link <Text as="span" color="gray.400">(optional — live online URL)</Text></FormLabel>
                        <Input size="sm" value={versionForm.external_url}
                          onChange={e => setVersionForm(p => ({ ...p, external_url: e.target.value }))}
                          placeholder="https://…" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>What changed? <Text as="span" color="gray.400">(version notes)</Text></FormLabel>
                        <Textarea size="sm" rows={2} value={versionForm.change_notes}
                          onChange={e => setVersionForm(p => ({ ...p, change_notes: e.target.value }))}
                          placeholder="e.g. Updated schedule for week 3, revised budget figures..." />
                      </FormControl>
                      <AssigneeEditor value={versionForm} setter={setVersionForm} />
                      <HStack justify="flex-end" w="100%">
                        <Button size="xs" variant="ghost" onClick={() => setVersioningId(null)}>Cancel</Button>
                        <Button size="xs" bg="orange.500" color="white" _hover={{ bg: 'orange.600' }}
                          isLoading={saving || uploading} onClick={() => handleVersion(current)}>
                          Upload v{(current.version || 1) + 1}
                        </Button>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* Version history */}
                {history.length > 0 && (
                  <Box>
                    <Flex
                      px={3} py={1.5} cursor="pointer" bg="gray.50" align="center" gap={1}
                      onClick={() => setExpandedHistory(p => ({ ...p, [current.name]: !p[current.name] }))}
                      _hover={{ bg: 'gray.100' }}
                    >
                      {expandedHistory[current.name] ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                      <Text fontSize="10px" color="gray.500">{history.length} previous version{history.length !== 1 ? 's' : ''}</Text>
                    </Flex>
                    <Collapse in={!!expandedHistory[current.name]}>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr bg="gray.50">
                            <Th px={3} fontSize="9px">Ver</Th>
                            <Th px={3} fontSize="9px">File</Th>
                            <Th px={3} fontSize="9px">Changes</Th>
                            <Th px={3} fontSize="9px">Uploaded</Th>
                            <Th px={3} fontSize="9px">By</Th>
                            <Th px={3} fontSize="9px">Assignees</Th>
                            <Th px={3} fontSize="9px"></Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {history.map(ver => (
                            <Tr key={ver.id} _hover={{ bg: 'gray.50' }} opacity={0.75}>
                              <Td px={3}><Badge colorScheme="gray" fontSize="9px">v{ver.version}</Badge></Td>
                              <Td px={3}>
                                {ver.stored_name ? (
                                  <Button as="a" href={fileUrl(ver)} target="_blank" size="xs" variant="link"
                                    colorScheme="blue" download={ver.original_name} fontSize="10px">
                                    {ver.original_name || 'Download'}
                                  </Button>
                                ) : <Text fontSize="10px" color="gray.400">No file</Text>}
                              </Td>
                              <Td px={3} maxW="200px">
                                <Text fontSize="10px" color="gray.600" fontStyle={ver.change_notes ? 'italic' : 'normal'}>
                                  {ver.change_notes || '—'}
                                </Text>
                              </Td>
                              <Td px={3} whiteSpace="nowrap"><Text fontSize="9px" color="gray.500">{fmtDate(ver.created_at)}</Text></Td>
                              <Td px={3}><Text fontSize="9px" color="gray.500">{ver.created_by_name || '—'}</Text></Td>
                              <Td px={3}>
                                <Flex gap={1}>
                                  {(ver.users || []).map(u => <Badge key={u.user_id} colorScheme="teal" fontSize="8px">{u.display_name || u.username}</Badge>)}
                                  {(ver.contacts || []).map(c => <Badge key={c.contact_id} colorScheme="purple" fontSize="8px">{c.first_name}</Badge>)}
                                </Flex>
                              </Td>
                              <Td px={3}>
                                <IconButton icon={<FiTrash2 size={11} />} size="xs" variant="ghost" colorScheme="red"
                                  onClick={() => handleDelete(ver.id)} aria-label="Delete version" />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Collapse>
                  </Box>
                )}
              </Box>
              );
            })}
              </VStack>
            </TabPanel>

            {/* ── Tab 2: All Files ── */}
            <TabPanel px={4}>
              {allFilesLoading ? (
                <Text fontSize="12px" color="gray.400" textAlign="center" py={8}>Loading all files…</Text>
              ) : allFiles.length === 0 ? (
                <Box textAlign="center" py={10}>
                  <Text fontSize="13px" color="gray.500">No files found for this {taskId ? 'task' : 'project'}.</Text>
                  <Text fontSize="10px" color="gray.400" mt={1}>Files attached to documents, invoices, notes and communications will appear here.</Text>
                </Box>
              ) : (
                <VStack spacing={1} align="stretch">
                  <HStack spacing={3} mb={2} flexWrap="wrap">
                    {Object.entries(SOURCE_CONFIG).map(([k, v]) => (
                      <HStack key={k} spacing={1} fontSize="9px" color="gray.500">
                        <Box w="8px" h="8px" borderRadius="full" bg={`${v.color}.400`} />
                        <Text>{v.label}</Text>
                      </HStack>
                    ))}
                  </HStack>

                  {allFiles.map((file, idx) => {
                    const src = SOURCE_CONFIG[file.source_type] || { label: file.source_type, color: 'gray', icon: '📄' };
                    const url = file.stored_name && file.folder ? `/api/files/${file.folder}/${file.stored_name}` : null;
                    return (
                      <Flex key={`${file.source_type}-${file.file_id}-${idx}`}
                        p={2} border="1px" borderColor="gray.100" borderRadius="md"
                        bg="white" _hover={{ bg: 'gray.50', borderColor: `${src.color}.200` }}
                        align="center" gap={3} transition="all 0.1s">
                        <Box w="3px" h="36px" borderRadius="full" bg={`${src.color}.400`} flexShrink={0} />
                        <Text fontSize="18px" flexShrink={0}>{mimeIcon(file.mime_type)}</Text>
                        <Box flex={1} minW={0}>
                          <Flex align="center" gap={2} mb={0.5}>
                            <Text fontSize="11px" fontWeight="700" color="gray.800" noOfLines={1}>{file.original_name}</Text>
                            <Badge colorScheme={src.color} fontSize="8px" flexShrink={0}>{src.icon} {src.label}</Badge>
                            {file.doc_version && <Badge colorScheme="teal" fontSize="8px" variant="outline" flexShrink={0}>v{file.doc_version}</Badge>}
                          </Flex>
                          <HStack spacing={2} fontSize="9px" color="gray.400" flexWrap="wrap">
                            <Text noOfLines={1} maxW="200px">{file.source_name}</Text>
                            {file.source_date && <Text>· {fmtDate2(file.source_date)}</Text>}
                            {file.file_size > 0 && <Text>· {fmtSize(file.file_size)}</Text>}
                            {file.uploaded_by && <Text>· {file.uploaded_by}</Text>}
                          </HStack>
                        </Box>
                        <HStack spacing={1} flexShrink={0}>
                          {url && (
                            <Tooltip label="Download / View" fontSize="xs" hasArrow>
                              <IconButton as="a" href={url} target="_blank" download={file.original_name}
                                icon={<FiDownload size={13} />} size="xs" variant="ghost" colorScheme="blue" aria-label="Download" />
                            </Tooltip>
                          )}
                          {file.source_type !== 'document' && (
                            <Tooltip label={`Go to ${src.label}`} fontSize="xs" hasArrow>
                              <IconButton icon={<FiExternalLink size={13} />} size="xs" variant="ghost" colorScheme={src.color}
                                onClick={() => handleNavigateToSource(file)} aria-label="Go to source" />
                            </Tooltip>
                          )}
                          <Menu>
                            <MenuButton as={IconButton} icon={<FiMoreVertical size={13} />} size="xs" variant="ghost" aria-label="More" />
                            <MenuList fontSize="12px" minW="180px" zIndex={9999}>
                              {url && <MenuItem as="a" href={url} target="_blank" download={file.original_name} icon={<FiDownload size={12} />}>Download</MenuItem>}
                              {url && <MenuItem as="a" href={url} target="_blank" icon={<FiExternalLink size={12} />}>Open in new tab</MenuItem>}
                              {file.source_type !== 'document' && (
                                <MenuItem icon={<FiExternalLink size={12} />} onClick={() => handleNavigateToSource(file)}>Go to {src.label}</MenuItem>
                              )}
                              <MenuItem icon={<FiPlus size={12} />} onClick={() => handleAddAsVersion(file)}>Add as Document Version</MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} mr="auto" fontSize="10px" color="gray.400">
            <Text>{families.length} document{families.length !== 1 ? 's' : ''}</Text>
            <Text>·</Text>
            <Text>{allFiles.length} total file{allFiles.length !== 1 ? 's' : ''}</Text>
          </HStack>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Right-click context menu: move document to a folder */}
    {ctxMenu && (
      <>
        <Box position="fixed" inset={0} zIndex={10010} onClick={() => setCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
        <Box position="fixed" top={`${ctxMenu.y}px`} left={`${ctxMenu.x}px`} zIndex={10011}
          bg="white" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="xl" py={1} minW="200px" maxH="320px" overflowY="auto">
          <Text px={3} py={1} fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase">Move to</Text>
          <Box px={2} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }}
            onClick={async () => { const d = ctxMenu.doc; setCtxMenu(null); await moveDocToFolder(d.id, null); }}>
            <Flex align="center" gap={2}><FiCornerUpRight size={12} /> Unfiled</Flex>
          </Box>
          {folders.map(f => (
            <Box key={f.id} px={2} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
              onClick={async () => { const d = ctxMenu.doc; setCtxMenu(null); await moveDocToFolder(d.id, f.id); }}>
              <Flex align="center" gap={2}><Box color="blue.500"><FiFolder size={12} /></Box>{f.name}{String(ctxMenu.doc.folder_id) === String(f.id) && <Badge ml="auto" colorScheme="blue" fontSize="8px">current</Badge>}</Flex>
            </Box>
          ))}
          <Divider my={1} />
          <Box px={2} py={1.5} fontSize="12px" cursor="pointer" color="teal.600" fontWeight="600" _hover={{ bg: 'teal.50' }}
            onClick={() => { const d = ctxMenu.doc; setCtxMenu(null); setFolderModal({ name: '', description: '', moveDocId: d.id }); }}>
            <Flex align="center" gap={2}><FiFolderPlus size={13} /> Create New Folder…</Flex>
          </Box>
        </Box>
      </>
    )}

    {/* Create / edit folder modal */}
    <Modal isOpen={!!folderModal} onClose={() => setFolderModal(null)} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{folderModal?.editId ? 'Edit Folder' : 'New Folder'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
              <Input size="sm" autoFocus value={folderModal?.name || ''}
                onChange={e => setFolderModal(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter' && folderModal?.name.trim()) saveFolder(); }}
                placeholder="e.g. Contracts" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Description <Text as="span" color="gray.400">(optional)</Text></FormLabel>
              <Textarea size="sm" rows={2} value={folderModal?.description || ''}
                onChange={e => setFolderModal(p => ({ ...p, description: e.target.value }))}
                placeholder="What goes in this folder?" />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={() => setFolderModal(null)}>Cancel</Button>
          <Button size="sm" colorScheme="teal" isLoading={savingFolder} isDisabled={!folderModal?.name?.trim()} onClick={saveFolder}>
            {folderModal?.editId ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}
